import { useState } from 'react';
import { API_URL, API_VERSION } from '@constants';
import { useData } from '@hooks/useData';
import { paths } from '../../types/api';
import { ApiError, useApiConfig } from '@hooks/useApiConfig';

const ENDPOINT_URL = '/users/getUser';

const ENDPOINT_URL_WITH_VERSION = `${API_VERSION}${ENDPOINT_URL}` as const;
type url = typeof ENDPOINT_URL_WITH_VERSION;

export interface GetUserSuccessResponse {
    data: paths[url]['get']['responses']['200']['content']['application/json'];
}

/**
 * Requests endpoint to get user info.
 * Then saves it in Data Context.
 */
export const useGetUser = () => {
    const { getHeaders, handleCommonErrors } = useApiConfig();
    const { setUser } = useData();
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [lastResponseTime, setLastResponseTime] = useState<Date | null>(null);

    const request = async () => {
        setLoading(true);
        setError(null);

        try {
            const response = await fetch(`${API_URL}${ENDPOINT_URL}`, {
                method: 'GET',
                headers: getHeaders(),
            });

            if (!response.ok) {
                const json = await response.json();
                const error = new Error(json.error || '') as ApiError;
                error.statusCode = response.status;
                throw error;
            }

            const data: GetUserSuccessResponse = await response.json();

            setUser(data);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        } catch (err: any) {
            handleCommonErrors(err);
            err.message && setError(err.message);
        } finally {
            setLoading(false);
            setLastResponseTime(new Date());
        }
    };

    return { request, loading, error, lastResponseTime };
};
